import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Film, Trash2, Calendar, FolderOpen, User, Shield, LogOut, Settings, HelpCircle } from "lucide-react";

import { getApiUrl } from '@/config/api';

const API = getApiUrl();

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin, getAuthHeaders } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newProject, setNewProject] = useState({ name: "", description: "" });
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Logged out");
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: getAuthHeaders()
      });
      setProjects(response.data);
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast.error("Please enter a project name");
      return;
    }
    try {
      const response = await axios.post(`${API}/projects`, newProject, {
        headers: getAuthHeaders()
      });
      setProjects([...projects, response.data]);
      setNewProject({ name: "", description: "" });
      setDialogOpen(false);
      toast.success("Project created!");
      navigate(`/project/${response.data.id}`);
    } catch (error) {
      toast.error("Failed to create project");
    }
  };

  const handleDeleteProject = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success("Project deleted");
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#09090b] p-8 animate-fadeIn">
      {/* Header */}
      <header className="max-w-6xl mx-auto mb-12">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
              <Film className="w-5 h-5 text-indigo-400" />
            </div>
            <h1 className="text-3xl font-bold text-white tracking-tight">
              anim8pro
            </h1>
          </div>
          
          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 text-zinc-400 hover:text-white" data-testid="user-menu-btn">
                <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
                <span className="hidden sm:inline">{user?.name || user?.email}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 min-w-[180px]">
              <div className="px-2 py-1.5 text-sm text-zinc-400">
                {user?.email}
              </div>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem 
                className="cursor-pointer text-zinc-300 focus:bg-zinc-800 focus:text-white"
                onClick={() => navigate("/account")}
                data-testid="account-menu-item"
              >
                <Settings className="w-4 h-4 mr-2" />
                Account Settings
              </DropdownMenuItem>
              {isSuperAdmin && (
                <DropdownMenuItem 
                  className="cursor-pointer text-zinc-300 focus:bg-zinc-800 focus:text-white"
                  onClick={() => navigate("/super-admin")}
                  data-testid="super-admin-menu-item"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Admin Panel
                </DropdownMenuItem>
              )}
              <DropdownMenuItem 
                className="cursor-pointer text-zinc-300 focus:bg-zinc-800 focus:text-white"
                onClick={() => navigate("/help")}
                data-testid="help-menu-item"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Help Desk
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem 
                className="cursor-pointer text-red-400 focus:bg-zinc-800 focus:text-red-300"
                onClick={handleLogout}
                data-testid="logout-menu-item"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <p className="text-zinc-400 text-lg mt-2">
          Your art. Accelerated.
        </p>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-semibold text-white">Your Projects</h2>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-project-btn"
                className="bg-indigo-500 hover:bg-indigo-600 text-white gap-2 glow-primary"
              >
                <Plus className="w-4 h-4" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white">Create New Project</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-zinc-300">Project Name</Label>
                  <Input
                    id="name"
                    data-testid="project-name-input"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    placeholder="My Animation Project"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-zinc-300">Description (optional)</Label>
                  <Textarea
                    id="description"
                    data-testid="project-description-input"
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                    placeholder="A short description of your animation..."
                    className="bg-zinc-800 border-zinc-700 text-white resize-none"
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="ghost" className="text-zinc-400">Cancel</Button>
                </DialogClose>
                <Button 
                  data-testid="confirm-create-project-btn"
                  onClick={handleCreateProject}
                  className="bg-indigo-500 hover:bg-indigo-600"
                >
                  Create Project
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full spinner" />
          </div>
        ) : projects.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
                <FolderOpen className="w-8 h-8 text-zinc-500" />
              </div>
              <p className="text-zinc-400 text-lg mb-2">No projects yet</p>
              <p className="text-zinc-500 text-sm mb-6">Create your first animation project to get started</p>
              <Button 
                onClick={() => setDialogOpen(true)}
                className="bg-indigo-500 hover:bg-indigo-600 gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Project
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <Card
                key={project.id}
                data-testid={`project-card-${index}`}
                onClick={() => navigate(`/project/${project.id}`)}
                className="project-card bg-zinc-900/50 border-zinc-800 cursor-pointer group animate-slideUp"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                      <Film className="w-5 h-5 text-indigo-400" />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`delete-project-${index}`}
                      onClick={(e) => handleDeleteProject(e, project.id)}
                      className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <CardTitle className="text-white text-lg mt-3">{project.name}</CardTitle>
                  {project.description && (
                    <CardDescription className="text-zinc-400 line-clamp-2">
                      {project.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-zinc-500 text-sm">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(project.created_at)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Footer with Help Link */}
      <footer className="max-w-6xl mx-auto mt-12 pb-8">
        <div className="flex items-center justify-center gap-6 text-sm text-zinc-500">
          <button 
            onClick={() => navigate("/help")}
            className="flex items-center gap-2 hover:text-indigo-400 transition-colors"
            data-testid="footer-help-link"
          >
            <HelpCircle className="w-4 h-4" />
            Help Desk
          </button>
          <span className="text-zinc-700">•</span>
          <span>anim8pro v3.7</span>
        </div>
      </footer>
    </div>
  );
}
